"use client";

import { c as _c } from "react/compiler-runtime";
import { useAtom } from "jotai";
import { AnimatePresence } from "motion/react";
import { type ComponentType, useCallback, useEffect, useRef, useState } from "react";
import { chatOpenAtom } from "@/lib/atoms/chat-atoms";
import { usePreferencesHydrated, usePreferencesStore } from "@/lib/stores/preferences-store";
import { ChatFab } from "./chat-fab";
interface LoadedChatClientProps {
  suggestions?: string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReady: () => void;
}
type ChatClientModule = {
  ChatClient: ComponentType<LoadedChatClientProps>;
};
type ChatClientLoader = () => Promise<ChatClientModule>;
const loadChatClient: ChatClientLoader = () => import("./chat-client");
export function ChatClientLazy(t0) {
  const $ = _c(31);
  const {
    suggestions,
    loader: t1
  } = t0;
  const loader = t1 === undefined ? loadChatClient : t1;
  const hydrated = usePreferencesHydrated();
  const hideAiFeatures = usePreferencesStore(_temp);
  const setHideAiFeatures = usePreferencesStore(_temp2);
  const [open, setOpen] = useAtom(chatOpenAtom);
  const [ChatClient, setChatClient] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const loadingRef = useRef(false);
  let t2;
  let t3;
  if ($[0] !== hideAiFeatures || $[1] !== hydrated || $[2] !== setHideAiFeatures) {
    t2 = () => {
      if (!hydrated) {
        return;
      }
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get("show_ai") === "1" && hideAiFeatures) {
        setHideAiFeatures(false);
        urlParams.delete("show_ai");
        const newUrl = urlParams.toString() === "" ? window.location.pathname : `${window.location.pathname}?${urlParams.toString()}`;
        window.history.replaceState({}, "", newUrl);
      }
    };
    t3 = [hydrated, hideAiFeatures, setHideAiFeatures];
    $[0] = hideAiFeatures;
    $[1] = hydrated;
    $[2] = setHideAiFeatures;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[5] !== hideAiFeatures || $[6] !== setOpen) {
    t4 = () => {
      if (hideAiFeatures) {
        setOpen(false);
      }
    };
    t5 = [hideAiFeatures, setOpen];
    $[5] = hideAiFeatures;
    $[6] = setOpen;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  if ($[9] !== ChatClient || $[10] !== loader || $[11] !== setOpen) {
    t6 = () => {
      if (ChatClient) {
        setOpen(_temp3);
        return;
      }
      if (loadingRef.current) {
        return;
      }
      loadingRef.current = true;
      setLoading(true);
      setLoadError(false);
      setOpen(true);
      loader().then(module => {
        setChatClient(() => module.ChatClient);
      }).catch(() => {
        loadingRef.current = false;
        setLoading(false);
        setLoadError(true);
        setOpen(false);
      });
    };
    $[9] = ChatClient;
    $[10] = loader;
    $[11] = setOpen;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const handleActivation = t6;
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      loadingRef.current = false;
      setLoading(false);
    };
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const handleReady = t7;
  if (!hydrated) {
    return null;
  }
  let t8;
  if ($[14] !== handleActivation || $[15] !== hideAiFeatures || $[16] !== loading) {
    t8 = !hideAiFeatures && <ChatFab loading={loading} onClick={handleActivation} />;
    $[14] = handleActivation;
    $[15] = hideAiFeatures;
    $[16] = loading;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t8) {
    t9 = <AnimatePresence>{t8}</AnimatePresence>;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== loadError) {
    t10 = loadError && <p className="sr-only" aria-live="polite">Chat failed to load. Try again.</p>;
    $[20] = loadError;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== ChatClient || $[23] !== open || $[24] !== setOpen || $[25] !== suggestions) {
    t11 = ChatClient && <ChatClient suggestions={suggestions} open={open} onOpenChange={setOpen} onReady={handleReady} />;
    $[22] = ChatClient;
    $[23] = open;
    $[24] = setOpen;
    $[25] = suggestions;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t10 || $[28] !== t11 || $[29] !== t9) {
    t12 = <>{t9}{t10}{t11}</>;
    $[27] = t10;
    $[28] = t11;
    $[29] = t9;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  return t12;
}
function _temp3(current) {
  return !current;
}
function _temp2(s_0) {
  return s_0.setHideAiFeatures;
}
function _temp(s) {
  return s.hideAiFeatures;
}